<script lang="ts">
import '@/configs/google-maps';
import type { StripAtSuffixes } from '@scaffold/common';
import settings from '@scaffold/common/settings';

import markerImg from '@/assets/images/img-marker.png';
import mapImg from '@/assets/images/map.png';
import GoogleMap from '@/components/GoogleMap.vue';
import Page from '@/components/Page.vue';
import ThirdPartyEmbed from '@/components/ThirdPartyEmbed.vue';
import { locale } from '@/context';
import { exportRoute } from '@/core/routing';
import { spanFirst } from '@/core/utils';

function getPage(locale: string) {
  const loaders = {
    'en-GB': import('./page.json?locale=en-GB'),
    'de-DE': import('./page.json?locale=de-DE'),
  };
  return (loaders[locale as keyof typeof loaders] || loaders['en-GB']).then(
    (mod) => mod.default as StripAtSuffixes<typeof import('./page.json')>,
  );
}

export const { defineRouteMeta } = exportRoute();

const { useRouteMeta } = defineRouteMeta(async () => {
  return await getPage(locale.value);
});

function onMapReady(map: google.maps.Map) {
  const { Size, Point, Marker } = google.maps;

  map.set('styles', [
    {
      featureType: 'all',
      elementType: 'all',
      stylers: [{ saturation: -100 }, { gamma: 0.5 }],
    },
    {
      featureType: 'road',
      elementType: 'all',
      stylers: [{ color: '#e6e6e6' }],
    },
  ]);

  new Marker({
    position: {
      lat: settings.contact.location.lat,
      lng: settings.contact.location.lng,
    },
    map,
    icon: {
      url: markerImg,
      size: new Size(51, 62),
      origin: new Point(0, 0),
      anchor: new Point(25, 62),
    },
  });
}
</script>

<script setup lang="ts">
const page = useRouteMeta();
</script>

<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <Page :class="$style.root" :title="page.title">
    <div class="hh">
      <h2>{{ page.title }}</h2>
    </div>

    <div :class="$style['chars-1']">
      <span></span>

      <div>
        <h4 v-html="spanFirst($t`Opening hours`, 4)" />
        <p class="text-pre-wrap">{{ page.open_hours }}</p>
      </div>
    </div>

    <div :class="$style['chars-2']" class="mb-3">
      <p class="dotted-line">
        <span>{{ $t`Mobile` }}</span>
        <b
          ><a :href="`tel:${settings.contact.mobile}`" target="_blank">{{
            settings.contact.mobile_text
          }}</a></b
        >
      </p>

      <p class="dotted-line">
        <span>{{ $t`Tel` }}</span>
        <b
          ><a :href="`tel:${settings.contact.landline}`" target="_blank">{{
            settings.contact.landline_text
          }}</a></b
        >
      </p>

      <p class="dotted-line">
        <span>{{ $t`Email` }}</span>
        <b
          ><i
            ><a :href="`mailto:${settings.contact.email}`" target="_blank">{{
              settings.contact.email
            }}</a></i
          ></b
        >
      </p>

      <p class="dotted-line">
        <span>{{ $t`Address` }}</span>
        <b
          ><i
            ><a
              :href="
                'https://maps.google.com/?q=' +
                encodeURIComponent(settings.contact.address)
              "
              target="_blank"
              >{{ settings.contact.address }}</a
            ></i
          ></b
        >
      </p>
    </div>

    <div :class="$style['chars-3']" class="clearfix">
      <blockquote class="edge lb">
        <div v-html="page.location_text" />
      </blockquote>

      <div :class="$style.googleMap">
        <ThirdPartyEmbed
          id="google-maps"
          name="Google Maps"
          :image="mapImg"
          position="bottom"
        >
          <GoogleMap :center="settings.contact.location" @ready="onMapReady" />
        </ThirdPartyEmbed>
      </div>

      <div :class="$style.social">
        <a
          :class="$style.fb"
          href="https://www.facebook.com/castalianspring/"
          target="_blank"
        />
        <a
          :class="$style.in"
          href="https://instagram.com/castalianspring/"
          target="_blank"
        />
        <a
          :class="$style.yt"
          href="https://www.youtube.com/channel/UCegXBpjKgBvyfxvVYiyWe1g/videos"
          target="_blank"
        />
      </div>
    </div>
  </Page>
</template>

<style lang="scss" module>
.root {
  .chars-1 {
    margin-bottom: 1rem;

    > div p {
      font-style: italic;
      font-weight: bold;
      font-size: 1.4rem;
    }

    @include media-breakpoint-up(sm) {
      position: relative;
      width: 170px;
      height: 170px;
      background: url('@/assets/images/img-clock.png') no-repeat;
      margin: 0 auto 3rem;

      > div {
        position: absolute;
        white-space: nowrap;
        text-align: right;
        top: 77px;
        left: 85px;

        h4 {
          letter-spacing: 1.24em;

          span {
            color: $white;
          }
        }
      }

      > span {
        &::before {
          content: '×';
          font-size: 1rem;
          line-height: 1rem;
          display: block;
          position: absolute;
          bottom: 0;
          left: 0;
        }

        &::after {
          content: '×';
          font-size: 1rem;
          line-height: 1rem;
          display: block;
          position: absolute;
          bottom: 0;
          right: 0;
        }
      }

      &::before {
        content: '×';
        font-size: 1rem;
        line-height: 1rem;
        display: block;
        position: absolute;
        top: 0;
        left: 0;
      }

      &::after {
        content: '×';
        font-size: 1rem;
        line-height: 1rem;
        display: block;
        position: absolute;
        top: 0;
        right: 0;
      }
    }
  }

  .chars-2 {
    :global(.dotted-line) {
      margin-bottom: 1.5rem;

      &:first-child {
        margin-bottom: 0.5rem;
      }
    }

    a {
      text-decoration: none;
    }

    b {
      display: inline-block;
      min-width: 16em;
      font-size: 1rem;
      font-family: Intro, sans-serif;
      letter-spacing: 0.31em;
      line-height: 1;

      i {
        font-family: 'PT Sans', sans-serif;
        letter-spacing: initial;
      }
    }

    span {
      font-family: Intro, sans-serif;
      text-transform: uppercase;
      letter-spacing: 0.7em;
      font-size: 0.5rem;
      font-weight: 500;
    }

    @include media-breakpoint-up(sm) {
      width: $content-width;
      margin: 0 auto;

      b {
        font-size: 1.3rem;
      }
    }

    @include media-breakpoint-down(xs) {
      // label stacked above its value, no columns
      :global(.dotted-line) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        > * {
          padding: 0;
        }

        > :first-child {
          margin-bottom: 0.5em;
        }

        &::before {
          display: none;
        }
      }
    }
  }

  .chars-3 {
    position: relative;

    blockquote::after {
      display: none;
    }

    .googleMap {
      height: 30rem;
      margin-bottom: 1rem;
    }

    .GoogleMap {
      height: 30rem;
    }

    .social {
      text-align: center;
    }

    @include media-breakpoint-up(sm) {
      margin: 0 auto;
      width: $content-width;
    }

    @include media-breakpoint-up(md) {
      .googleMap {
        width: 30rem;
        z-index: 0;
        float: left;
        clear: left;
        margin-top: -2rem;
      }

      blockquote {
        > div {
          position: relative;
          z-index: 1;
        }

        &::after {
          display: block;
          z-index: 1;
          width: calc(16rem - 3px);
          height: 1rem;
        }

        width: 26rem;
        position: relative;
        float: right;
        margin-right: -6rem;
      }

      .social {
        float: right;
        clear: right;
        margin-right: -5rem;
      }
    }
  }

  .social {
    word-spacing: 0.5rem;

    > * {
      display: inline-block;
      position: relative;
      width: 34px;
      height: 34px;
      background: url('@/assets/images/btn-social-black.png') repeat-y;
      transition: background-position-y 0.3s ease;

      &:hover,
      &:focus {
        background-position-y: 34px;
      }

      &.in {
        background-position-x: -45px;
      }

      &.yt {
        background-position-x: -180px;
      }
    }
  }
}
</style>
